import {
  Button,
  Card,
  Col,
  Collapse,
  CollapseProps,
  Divider,
  Form,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Switch,
  Tag,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { ApiService } from "api/api.service";
import { ApplicationDto, ManagerApprovalStatus, PaymentEntryTable, T_CustomStrategy } from "dtos/application.dto";
import React, { useEffect, useRef, useState } from "react";
import { TbEdit, TbPlus, TbSend, TbTrash } from "react-icons/tb";
import StrategyPanel from "./StrategyPanel";
import { FaRegSave } from "react-icons/fa";
import { getDebtBasedWeeks, parseNumericToInternationalFormat } from "utils/functions";
import { CustomStrategyConstants } from "utils/constants";
import { queryClient } from "utils/queryClient";
import { getCalculationFields, getMinimumWeeklyBudget } from "./functions";

type CustomStrategy = {
  weekly_budget: number;
  initial_payment: number;
  initial_payment_timeline: number;
  net_commission: number;
  commission_timeline: number;
  creditor_estimate: number;
  weeks: number;
  include_commission_payment: boolean;
  initial_payment_charge: number;
  creditor_estimate_split: { amount: number; weeks: number }[];
  weekly_commission_payment?: number;
  bias_amount?: number;
  display_timeline: number;
  program_part: number;
  success_part: number;
};

const CustomModal = ({
  isOpen,
  onClose,
  appkey,
}: {
  isOpen: boolean;
  onClose: () => unknown;
  appkey: string | undefined;
}) => {
  const [form] = Form.useForm();
  const [activeKey, setActiveKey] = useState<string | string[]>();
  const addRef = useRef<(() => void) | null>(null);
  const applicationResponse = ApiService.application.useGetByAppKey(appkey);
  const saveCustomStrategy = ApiService.application.useSaveCustomStrategies();
  const getCustomStrategy = ApiService.application.useGetCustomStrategies(appkey);
  const customStrategies = getCustomStrategy.data?.result.v1;
  const [isDisabled, setDisabled] = useState(false);
  const [totalStrategies, setTotalStrategies] = useState(0);
  const sendStrategy = ApiService.application.useSendCustomStrategies();
  const rejectApproval = ApiService.application.useRejectManagerApproval();
  const hasSavedOnce = useRef(false);
  const [hasSplit, setHasSplit] = useState<{ idx: number; split: boolean }>();

  useEffect(() => {
    if (!isOpen || !customStrategies) return;

    if (customStrategies.length > 0) {
      hasSavedOnce.current = true;
    }

    let splitInfo: { idx: number; split: boolean } | undefined;

    const hydrated = customStrategies.map((c, idx) => {
      const missingSplit = c.program_part == null || c.success_part == null;

      if (missingSplit && !splitInfo) {
        splitInfo = { idx, split: true };
      }

      return {
        ...c,
        program_part: c.program_part != null ? c.program_part : c.net_commission / 2,
        success_part: c.success_part != null ? c.success_part : c.net_commission / 2,
      };
    });

    setHasSplit(splitInfo);
    form.setFieldsValue({ strategies: hydrated });
    setTotalStrategies(hydrated.length);
  }, [isOpen, customStrategies]);

  const application = applicationResponse.data?.application as ApplicationDto | undefined;

  const totalDebt = application?.enrolled_debt ?? 0;
  const extra =
    application && application.total_lawsuits && application.total_lawsuits > 0
      ? CustomStrategyConstants.lawsuit_initial * application.total_lawsuits
      : CustomStrategyConstants.insurance_one_time;

  const weekly_budget: number = application?.approval_requested ? (application?.requested_budget ?? 100) : 100;
  // const weekly_budget: number = 700;

  // console.log({ weekly_budget });

  const {
    commission_timeline,
    creditor_estimate,
    creditor_estimate_split,
    include_commission_payment,
    initial_payment,
    initial_payment_charge,
    initial_payment_timeline,
    net_commission,
    weeks,
    weeklyCommissionPayment,
    weeklyInitialPayment,
  } = getCalculationFields(
    totalDebt,
    extra,
    weekly_budget,
    application?.total_lawsuits ?? 0,
    CustomStrategyConstants.bias_amount,
  );

  const program_part = CustomStrategyConstants.program_part;
  const success_part = CustomStrategyConstants.success_part;

  const initialValues: CustomStrategy = {
    weekly_budget,
    initial_payment_timeline,
    net_commission: program_part + success_part,
    commission_timeline,
    initial_payment,
    creditor_estimate,
    weeks,
    include_commission_payment,
    initial_payment_charge,
    creditor_estimate_split,
    bias_amount: CustomStrategyConstants.bias_amount,
    display_timeline: commission_timeline,
    program_part,
    success_part,
  };

  // console.log(initialValues);

  const handleCancel = () => {
    setTotalStrategies(customStrategies?.length ?? 0);
    setActiveKey(undefined);
    onClose();
  };

  const handleFormSubmission = async (values: { strategies: CustomStrategy[] }) => {
    try {
      const data = values.strategies;
      // console.log(data);

      if (data.length === 0 && !hasSavedOnce.current) {
        message.error("No plans have been added yet.");
        return;
      }
      console.log({ data });

      const body: T_CustomStrategy[] = data.map(({ ...rest }) => ({
        ...rest,
        original_weeks: getDebtBasedWeeks(totalDebt).total_weeks,
        bias_amount: rest?.bias_amount ?? CustomStrategyConstants.bias_amount,
        card_mdr: CustomStrategyConstants.card_mdr,
        initial_pay: rest.initial_payment_charge,
        insurance_monthly: CustomStrategyConstants.insurance_monthly,
        insurance_one_time: CustomStrategyConstants.insurance_one_time,
        lawsuit_initial: CustomStrategyConstants.lawsuit_initial,
        lawsuit_monthly: CustomStrategyConstants.lawsuit_monthly,
        multiplier: 1 + rest.net_commission / 100,
        pushback_factor: CustomStrategyConstants.pushback_factor,
        service_weekly: CustomStrategyConstants.service_weekly,
        total_interest: CustomStrategyConstants.total_interest,
      }));

      saveCustomStrategy.mutate(
        { appkey: appkey!, body },
        {
          onSuccess: async () => {
            hasSavedOnce.current = true;
            message.success("Strategies saved succesfully.");
            queryClient.invalidateQueries({ queryKey: ["application"] });
            setActiveKey(undefined);
            setHasSplit(undefined);
            // await queryClient.refetchQueries({ queryKey: ["get-strategy"] });
            // form.resetFields();
            // handleCancel();
          },
        },
      );
    } catch (error) {
      message.error("Something went wrong.");
      // console.log(error);
    }
  };

  return (
    <Modal
      open={isOpen}
      centered
      styles={{
        body: {
          height: 500,
          overflowY: "auto",
        },
        content: {
          paddingInline: 12,
        },
        header: {
          paddingInline: 24,
        },
        footer: {
          paddingInline: 12,
        },
      }}
      destroyOnHidden
      title={
        <>
          Manage custom strategies
          <div className="flex justify-between items-center flex-wrap gap-2">
            <p className="text-wrap my-4 text-lg">
              Customer Budget →{" "}
              {application?.approval_requested && application.approval_requested === true ? (
                <span className="text-green-500">
                  ${parseNumericToInternationalFormat(application?.requested_budget)}
                </span>
              ) : (
                <span className="text-red-500">N/A</span>
              )}
            </p>
            <div className="flex gap-2 items-center flex-row-reverse">
              {application?.approval_requested &&
              application.approval_requested === true &&
              application.manager_approval_status &&
              application.manager_approval_status !== ManagerApprovalStatus.REJECTED ? (
                <Popconfirm
                  title="Are you sure to reject the request?"
                  okText="Yes"
                  cancelText="No"
                  placement="bottomLeft"
                  onConfirm={() => {
                    rejectApproval.mutate(
                      { appkey: appkey as string, cancel: false },
                      {
                        onSuccess: () => {
                          message.success("Manager's approval has been rejected.");
                          applicationResponse.refetch();
                        },
                      },
                    );
                  }}
                >
                  <Button
                    danger
                    variant="outlined"
                    loading={rejectApproval.isPending && rejectApproval.variables.cancel === false}
                    size="small"
                  >
                    Reject Approval
                  </Button>
                </Popconfirm>
              ) : null}

              {application?.approval_requested && application.approval_requested === true ? (
                <Popconfirm
                  title="Are you sure to cancel the request?"
                  okText="Yes"
                  cancelText="No"
                  placement="bottomLeft"
                  onConfirm={() => {
                    rejectApproval.mutate(
                      { appkey: appkey as string, cancel: true },
                      {
                        onSuccess: () => {
                          message.success("Manager's approval has been cancelled.");
                          applicationResponse.refetch();
                        },
                      },
                    );
                  }}
                >
                  <Button
                    danger
                    variant="outlined"
                    loading={rejectApproval.isPending && rejectApproval.variables.cancel === true}
                    size="small"
                  >
                    Cancel Approval
                  </Button>
                </Popconfirm>
              ) : null}
            </div>
          </div>
        </>
      }
      onCancel={handleCancel}
      width={1000}
      loading={applicationResponse.isPending}
      footer={
        <div>
          <Divider />
          <div className="flex justify-between items-center">
            <Button key="add" color="green" variant="outlined" icon={<TbPlus />} onClick={() => addRef.current?.()}>
              Create New Plan
            </Button>
            <Button
              icon={<FaRegSave />}
              htmlType="submit"
              type="primary"
              onClick={() => form.submit()}
              disabled={isDisabled}
              loading={saveCustomStrategy.isPending}
            >
              {customStrategies?.length === 0 ? "Save plans" : "Save changes"}
            </Button>
            <Button
              type="primary"
              icon={<TbSend />}
              disabled={isDisabled || totalStrategies < 1}
              loading={sendStrategy.isPending}
              onClick={() => {
                sendStrategy.mutate(
                  { appkey: appkey! },
                  {
                    onSuccess: async () => {
                      message.success("Plans have been sent to debtor portal.");
                      await queryClient.refetchQueries({ queryKey: ["get-strategy"] });
                      form.resetFields();
                      handleCancel();
                    },
                  },
                );
              }}
            >
              Send plans
            </Button>
          </div>
        </div>
      }
    >
      {/* {application && application.approval_requested === false ? (
        <p className="text-center">No manager&#39;s approval requested by customer.</p>
      ) : null} */}
      <div>
        <Form form={form} name="custom_strategy" autoComplete="off" layout="vertical" onFinish={handleFormSubmission}>
          <Form.List name="strategies">
            {(fields, { add, remove }) => {
              addRef.current = () => {
                add(initialValues);
                setTotalStrategies((prev) => prev + 1);
                const nextIdx = fields.length;
                setTimeout(() => setActiveKey(String(nextIdx)), 0);
              };

              const items: CollapseProps["items"] = fields.map(({ key, name, ...rest }) => ({
                key: name,
                label: (
                  <Space className="flex justify-between w-full">
                    <span>
                      Custom Strategy {name + 1}{" "}
                      {name === application?.custom_plan_idx && (
                        <Tag color="yellow" className="ml-2">
                          USER SELECTED
                        </Tag>
                      )}
                    </span>

                    <div className="flex items-center gap-4">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(name);
                          setTotalStrategies((prev) => (prev < 1 ? 0 : prev - 1));
                          if (activeKey === String(name)) setActiveKey(undefined);
                        }}
                        size="small"
                        danger
                        icon={<TbTrash />}
                        disabled={application?.custom_plan_idx === name}
                      />
                    </div>
                  </Space>
                ),
                children: (
                  <StrategyPanel
                    name={name}
                    totalDebt={totalDebt}
                    extraAmount={extra}
                    remove={remove}
                    activeKey={activeKey}
                    setActiveKey={setActiveKey}
                    setDisabled={setDisabled}
                    totalLawsuits={application?.total_lawsuits ?? 0}
                    minWeeklyBudget={getMinimumWeeklyBudget(
                      initial_payment_timeline,
                      initial_payment,
                      // weekly_budget,
                      // weeklyCommissionPayment,
                      application?.total_lawsuits ?? 0,
                      {
                        commission_timeline,
                        initial_payment_charge,
                        net_commission,
                        totalDebt,
                      },
                    )}
                    hasSplit={hasSplit?.idx === name && hasSplit.split}
                  />
                ),
              }));

              return (
                <div className="px-2">
                  {totalStrategies > 0 ? (
                    <Collapse
                      bordered
                      activeKey={activeKey}
                      accordion
                      onChange={(key) => {
                        const isClosed = Array.isArray(key) ? key.length === 0 : key === "";
                        if (isClosed) {
                          setActiveKey(undefined);
                        } else {
                          setActiveKey(key);
                        }
                      }}
                      items={items}
                    />
                  ) : (
                    <p className="text-center">No custom strategies created yet.</p>
                  )}
                </div>
              );
            }}
          </Form.List>
        </Form>
      </div>
    </Modal>
  );
};

export default CustomModal;
